import type { UserDataQuery } from '#api/shared/@types/generated/backend/graphql-schema-types'

export type RoktAttributes = Record<string, number | string>

export const useRokt = () => {
  const { datadog } = useRuntimeConfig().public
  const isGtmEnabled = useIsFeatureFlagEnabled('gtm')
  if (!isGtmEnabled) return
  if (import.meta.server) return
  const restrictedRouteNames = ['/forgot-password', '/reset-password']
  const url = new URL(window.location.href)
  const route = useRoute()
  const loadPromise = useState<null | Promise<void>>('rokt-load-promise', () => null)

  const allowRoktPlacement = useState('allow-rokt-placement', () => false)

  const loadRokt = () => {
    if (!restrictedRouteNames.includes(String(url.pathname))) {
      if (window.mParticle) return Promise.resolve()

      if (loadPromise.value) return loadPromise.value

      loadPromise.value = new Promise((resolve) => {
        try {
          loadScript({
            options: {
              onerror: () => resolve(),
              onload: () => resolve(),
            },
            src: '/scripts/rokt.js',
          })
        } catch {
          resolve()
        }
      })

      return loadPromise.value
    }
  }

  const runRoktCommand = async <T>(
    fn: (rokt: typeof window.mParticle) => Promise<T> | T,
  ): Promise<T | undefined> => {
    if (!window.mParticle) {
      await loadRokt()
    }
    if (!window.mParticle) return
    return fn(window.mParticle)
  }

  const identify = async (email: string) => {
    await runRoktCommand(async (rokt) => {
      const other = await sha256(email)
      return new Promise<void>((resolve, reject) => {
        rokt.Identity.identify(
          {
            userIdentities: {
              other,
            },
          },
          (result) => {
            if (result.getUser()) resolve()
            reject()
          },
        )
      })
    })
  }

  const initUser = async (player: UserDataQuery['player']) => {
    await runRoktCommand(async () => {
      await identify(player.email)
      const currentUser = window.mParticle.Identity.getCurrentUser()
      currentUser?.setUserAttribute('country', 'US')
      currentUser?.setUserAttribute('currency', 'USD')
      currentUser?.setUserAttribute('language', player.locale.split('-')[0].toUpperCase())
      currentUser?.setUserAttribute('firstName', player.givenName)
      currentUser?.setUserAttribute('billingzipcode', player.address.zipCode)
      currentUser?.setUserAttribute('mobile', player.phone.number)
    })
  }

  const logConversion = async (
    conversionType: string,
    attributes?: {
      amount?: number
      confirmationref?: string
      currency?: string
      majorcat: string
      productname: string
    },
  ) => {
    await runRoktCommand((rokt) => {
      rokt.logEvent('conversion', 4, {
        conversiontype: conversionType,
        ...(attributes && {
          ...attributes,
        }),
      })
    })
  }

  const logView = async () => {
    if (restrictedRouteNames.includes(String(route.name))) return
    await runRoktCommand((rokt) => {
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      const attributes = {
        'referring-page': document.referrer,
        'screen_name': String(route.name),
        'url': url.toString(),
      }
      rokt.logPageView('page view', attributes)
    })
  }

  const setAttributes = async (attributes: RoktAttributes) => {
    await runRoktCommand((rokt) => {
      const currentUser = rokt.Identity.getCurrentUser()
      if (!currentUser) return
      Object.entries(attributes).forEach(([key, value]) => {
        currentUser.setUserAttribute(key, value)
      })
    })
  }

  const setPaymenttype = async (paymenttype: string) => {
    await setAttributes({ paymenttype })
  }

  const showPlacement = async (): Promise<undefined | { close: () => void }> =>
    await runRoktCommand(
      async (rokt) =>
        await rokt.Rokt.selectPlacements({
          // https://docs.rokt.com/developers/integration-guides/getting-started/ecommerce/ecommerce-sdk-integration/#5-show-a-placement
          identifier: datadog.env === 'prod' ? 'prod.rokt.conf' : 'stg.rokt.conf',
        }),
    )

  return {
    allowRoktPlacement,
    identify,
    initUser,
    loadRokt,
    logConversion,
    logView,
    setAttributes,
    setPaymenttype,
    showPlacement,
  }
}
